"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from "react";

import { cn } from "@/lib/utils";

const iconButtonVariants = cva(
  [
    "inline-flex items-center justify-center",
    "rounded-4",
    "border-none",
    "cursor-pointer",
    "transition-colors duration-200",
    "focus-visible:outline-none focus-visible:ring-0",
  ],
  {
    variants: {
      variant: {
        primary: [
          "bg-transparent",
          "hover:bg-gray-200",
          "active:bg-gray-400",
          "disabled:cursor-default disabled:bg-transparent disabled:text-gray-500 disabled:pointer-events-none",
        ],
        secondary: [
          "bg-gray-300",
          "hover:bg-gray-400",
          "active:bg-gray-500",
          "disabled:cursor-default disabled:bg-transparent disabled:text-gray-500 disabled:pointer-events-none",
        ],
      },
      size: {
        xsmall: "w-6 h-6 min-w-6 p-0",
        small: "w-8 h-8 min-w-8 p-1",
        medium: "w-10 h-10 min-w-10 p-2",
        large: "w-11 h-11 min-w-11 p-2.5",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "medium",
    },
  }
);

export interface IconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof iconButtonVariants> {
  icon: ReactNode;
  "aria-label": string;
}

const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, variant, size, icon, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn(iconButtonVariants({ variant, size }), className)}
      {...props}
    >
      {icon}
    </button>
  )
);
IconButton.displayName = "IconButton";

export { IconButton, iconButtonVariants };
